import React, { useState } from "react";
import ThemeToggle from "../ThemeToggle";
import logoDark from "../../assets/images/logo/dark-based-logo-long.svg";
import logoLight from "../../assets/images/logo/white-based-logo-long.svg";
import { Menu, X } from "react-feather";

/**
 * Shared wrapper for all public pages (Login, ForgotPassword, etc.)
 * Renders background orbs, a responsive navbar with logo and theme toggle,
 * and the page content centred on screen.
 */
const PublicPageLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = React.useState(document.documentElement.classList.contains("dark"));

  React.useLayoutEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "var(--page-bg)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative orbs */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 600,
          height: 600,
          top: -150,
          left: -150,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.08)",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 400,
          height: 400,
          bottom: -100,
          right: -100,
          borderRadius: "50%",
          background: "rgba(139,92,246,0.07)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 300,
          height: 300,
          top: "40%",
          left: "30%",
          borderRadius: "50%",
          background: "rgba(6,182,212,0.05)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      {/* Navbar */}
      <nav
        style={{
          position: "relative",
          zIndex: 100,
          background: "var(--navbar-bg)",
          borderBottom: "1px solid var(--navbar-border)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <div
          style={{
            maxWidth: "1400px",
            margin: "0 auto",
            padding: "0 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "64px",
          }}
        >
          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={isDark ? logoDark : logoLight}
              alt="HR Fusions"
              style={{
                height: "32px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </div>

          {/* Desktop Navigation */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                display: "none",
                width: 40,
                height: 40,
                borderRadius: "10px",
                border: "1px solid var(--glass-border)",
                background: "var(--icon-btn-bg)",
                color: "var(--text-primary)",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              className="mobile-menu-btn"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            style={{
              position: "fixed",
              top: 64,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              zIndex: 99,
            }}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Menu */}
        <div
          style={{
            position: "fixed",
            top: 64,
            right: mobileMenuOpen ? 0 : "-100%",
            width: "280px",
            maxWidth: "80vw",
            height: "calc(100vh - 64px)",
            background: "var(--sidebar-bg)",
            borderLeft: "1px solid var(--sidebar-border)",
            backdropFilter: "blur(20px)",
            transition: "right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 100,
            padding: "24px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                background: "var(--glass-bg)",
                border: "1px solid var(--glass-border)",
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "var(--text-secondary)",
                  margin: 0,
                }}
              >
                Welcome to HR Fusions
              </p>
            </div>
          </div>
        </div>
      </nav>

      {/* Page content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        {children}
      </div>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicPageLayout;
